import { Fragment, useMemo, useState } from "react";
import {
  ArrowLeft,
  Download,
  Eye,
  FileText,
  Image as ImageIcon,
  Link2,
} from "lucide-react";
import { API } from "@/lib/api";
import { withYear } from "@/lib/year";

const TYPES = [
  { key: "website", label: "Link Website", icon: Link2 },
  { key: "foto", label: "Foto", icon: ImageIcon },
  { key: "dokumen", label: "Dokumen", icon: FileText },
];

export default function RekapHasil({ tpl, subs, accounts, onBack, year }) {
  const [open, setOpen] = useState(null);

  const labelBy = useMemo(() => {
    const m = { ppid: "PPID" };
    for (const a of accounts || []) m[a.key] = a.label;
    // label pengunggah (termasuk akun admin PPID) tersimpan di entri
    for (const code of Object.keys(subs || {})) {
      for (const { key } of TYPES) {
        for (const e of subs[code][key] || []) {
          if (e.by && e.by_label) m[e.by] = e.by_label;
        }
      }
    }
    return m;
  }, [accounts, subs]);
  const labelOf = (k) => labelBy[k] || k;
  const assignLabel = (it) => {
    const a = it.assignedTo;
    if (a === "all") return "Semua Pengelola";
    const keys = Array.isArray(a) ? a : a ? [a] : [];
    if (!keys.length) return "PPID";
    return keys.map(labelOf).join(", ");
  };

  const leaves = tpl.items.filter((it) => it.leaf);

  const contributors = (code) => {
    const sub = subs[code] || {};
    const set = new Set();
    for (const { key } of TYPES) {
      (sub[key] || []).forEach((e) => set.add(e.by));
    }
    return [...set];
  };

  return (
    <div>
      <button
        data-testid="monev-rekap-back"
        onClick={onBack}
        className="inline-flex items-center gap-2 rounded-full border border-[#E3EBF3] bg-white px-4 py-2 text-xs font-bold text-slate-600 transition-colors duration-300 hover:bg-[#EFF6FC] hover:text-[#1479BF]"
      >
        <ArrowLeft aria-hidden="true" className="h-4 w-4" />
        Kembali
      </button>
      <h1 className="mt-6 font-display text-3xl font-semibold text-[#0B2440] md:text-4xl">
        Rekap Hasil Monev
      </h1>
      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-500">
        Seluruh isian e-Monev Tahun {year} beserta penomorannya. Klik tombol
        mata untuk melihat unggahan — dikelompokkan berdasarkan pengelola yang
        mengisi. Klik tombol unduh untuk mengunduh folder ZIP berisi seluruh
        unggahan pada satu isian (nama folder sesuai kode isian). Isian
        pengelola yang belum di-Submit (draft) tidak ditampilkan di halaman
        ini.
      </p>

      <div
        data-testid="monev-rekap-table"
        className="mt-8 overflow-x-auto rounded-3xl border border-[#E3EBF3] bg-white"
      >
        <table className="w-full min-w-[860px] text-left text-sm">
          <thead>
            <tr className="border-b border-[#E3EBF3] bg-[#F3F7FB] text-[10px] uppercase tracking-[0.1em] text-slate-500">
              <th className="px-4 py-3 font-bold">No</th>
              <th className="px-4 py-3 font-bold">Kode</th>
              <th className="px-4 py-3 font-bold">Item Isian</th>
              <th className="px-4 py-3 font-bold">Pengelola</th>
              <th className="px-4 py-3 font-bold">Keterisian</th>
              <th className="px-4 py-3 font-bold">Sudah Diunggah</th>
              <th className="px-4 py-3 font-bold">Unduh</th>
            </tr>
          </thead>
          <tbody>
            {leaves.map((it, i) => {
              const sub = subs[it.code] || {};
              const enabledKeys = TYPES.filter((t) => it.fields[t.key]?.enabled);
              const filled = enabledKeys.filter(
                (t) => (sub[t.key] || []).length > 0
              ).length;
              const bys = contributors(it.code);
              return (
                <Fragment key={it.code}>
                  <tr
                    data-testid={`monev-rekap-row-${it.code}`}
                    className="border-b border-[#E3EBF3] align-top"
                  >
                    <td className="px-4 py-3 text-xs text-slate-400">{i + 1}</td>
                    <td className="px-4 py-3 font-mono text-[11px] font-semibold text-[#1479BF]">
                      {it.code}
                    </td>
                    <td className="px-4 py-3 font-medium leading-snug text-[#0B2440]">
                      {withYear(it.title, year)}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {assignLabel(it)}
                    </td>
                    <td className="px-4 py-3 text-xs font-semibold text-slate-600">
                      {filled}/{enabledKeys.length} kolom
                    </td>
                    <td className="px-4 py-3">
                      {bys.length === 0 ? (
                        <span className="text-xs text-slate-300">—</span>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {bys.map((by) => {
                            const id = `${it.code}|${by}`;
                            const active = open === id;
                            return (
                              <button
                                key={by}
                                data-testid={`monev-eye-${it.code}-${by}`}
                                onClick={() => setOpen(active ? null : id)}
                                title={`Isian dari ${labelOf(by)}`}
                                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold transition-colors duration-200 ${
                                  active
                                    ? "border-[#1479BF] bg-[#1479BF] text-white"
                                    : "border-[#E3EBF3] text-slate-600 hover:bg-[#EFF6FC] hover:text-[#1479BF]"
                                }`}
                              >
                                <Eye aria-hidden="true" className="h-3.5 w-3.5" />
                                {labelOf(by)}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <a
                        data-testid={`monev-unduh-${it.code}`}
                        href={`${API}/api/monev/${it.code}/unduh?year=${year}`}
                        title={`Unduh folder isian ${it.code} (ZIP)`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#E3EBF3] px-2.5 py-1 text-[10px] font-bold text-slate-600 transition-colors duration-200 hover:bg-[#EFF6FC] hover:text-[#1479BF]"
                      >
                        <Download aria-hidden="true" className="h-3.5 w-3.5" />
                        Unduh
                      </a>
                    </td>
                  </tr>
                  {bys
                    .filter((by) => open === `${it.code}|${by}`)
                    .map((by) => (
                      <tr
                        key={`${it.code}-${by}`}
                        data-testid={`monev-rekap-detail-${it.code}-${by}`}
                        className="border-b border-[#E3EBF3] bg-[#FBFDFF]"
                      >
                        <td colSpan={7} className="px-4 py-4">
                          <p className="text-[10px] font-bold uppercase tracking-[0.1em] text-[#1479BF]">
                            Unggahan dari {labelOf(by)}
                          </p>
                          <div className="mt-3 grid gap-4 md:grid-cols-3">
                            {TYPES.map(({ key, label, icon: Icon }) => {
                              const list = (sub[key] || []).filter(
                                (e) => e.by === by
                              );
                              if (!list.length) return null;
                              return (
                                <div key={key}>
                                  <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.1em] text-slate-400">
                                    <Icon
                                      aria-hidden="true"
                                      className="h-3.5 w-3.5"
                                    />
                                    {label}
                                  </p>
                                  <ul className="mt-2 space-y-2">
                                    {list.map((e) => (
                                      <li key={e.id}>
                                        {key === "website" ? (
                                          <a
                                            href={e.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="block max-w-[240px] truncate text-xs font-medium text-[#1479BF] underline"
                                          >
                                            {e.url}
                                          </a>
                                        ) : key === "foto" ? (
                                          <a
                                            href={`${API}/api/monev/file/${e.id}`}
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            <img
                                              src={`${API}/api/monev/file/${e.id}`}
                                              alt={e.name}
                                              className="h-16 w-16 rounded-lg object-cover"
                                            />
                                          </a>
                                        ) : (
                                          <a
                                            href={`${API}/api/monev/file/${e.id}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="flex items-center gap-2 text-xs font-medium text-[#1479BF] underline"
                                          >
                                            <FileText
                                              aria-hidden="true"
                                              className="h-4 w-4 shrink-0"
                                            />
                                            <span className="max-w-[180px] truncate">
                                              {e.name}
                                            </span>
                                          </a>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}